import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AuthContext } from "../../AuthContext";
import { API_BASE_URL } from "../../config";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import {
  CurrentShiftsCard,
  DashboardLocationTitle,
  MonthlyOverviewCard,
  OpenRequestsRow,
  PlanningStatusCard,
} from "./DashboardCards";
import {
  asNumber,
  createDepartmentColorResolver,
  isFeatureVisible,
  monthValue,
  normalizeDashboardPayload,
} from "./dashboardModel";
import "../../i18n";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./dashboard.css";

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, selectedLocationId, locations = [] } = useContext(AuthContext);
  const token = localStorage.getItem("session_token");
  const initialMonth = useRef(monthValue());
  const [committedMonth, setCommittedMonth] = useState(initialMonth.current);
  const [dashboardRequest, setDashboardRequest] = useState({
    month: initialMonth.current,
    sequence: 0,
  });
  const [dashboard, setDashboard] = useState(null);
  const [loadedContextKey, setLoadedContextKey] = useState(null);
  const [approvalCount, setApprovalCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const currentContextKey = String(selectedLocationId ?? "") + "|" + String(token || "");
  const visibleDashboard = loadedContextKey === currentContextKey ? dashboard : null;
  const contextIsChanging = Boolean(dashboard && loadedContextKey !== currentContextKey);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const requestedMonth = dashboardRequest.month;
    const requestContextKey = String(selectedLocationId ?? "") + "|" + String(token || "");
    setLoading(true);
    setLoadError(null);

    axios.get(API_BASE_URL + "/api/web/dashboard", {
      params: { month: requestedMonth },
      headers: { Authorization: "Bearer " + token },
      signal: controller.signal,
    })
      .then((response) => {
        if (!active) return;
        setDashboard(normalizeDashboardPayload(response.data));
        setLoadedContextKey(requestContextKey);
        setCommittedMonth(requestedMonth);
        setLoading(false);
      })
      .catch((error) => {
        if (active && error?.code !== "ERR_CANCELED") {
          setLoadError({ month: requestedMonth });
          setLoading(false);
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [dashboardRequest.month, dashboardRequest.sequence, selectedLocationId, token]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setApprovalCount(null);
    axios.get(API_BASE_URL + "/api/web/approval/count", {
      headers: { Authorization: "Bearer " + token },
      signal: controller.signal,
    })
      .then((response) => {
        if (active) setApprovalCount(asNumber(response.data?.count));
      })
      .catch(() => {
        if (active) setApprovalCount(null);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [selectedLocationId, token]);

  const language = i18n.resolvedLanguage || i18n.language || "de";
  const locale = language.toLowerCase().startsWith("de") ? "de-CH" : language;
  const accessLevel = visibleDashboard?.accessLevel
    || user?.accessLevel
    || user?.access_level
    || "";
  const featureVisible = (key) => isFeatureVisible(
    visibleDashboard?.features || {},
    accessLevel,
    key
  );
  const showShiftplan = Boolean(visibleDashboard && featureVisible("page.shiftplan"));
  // /demand haengt am selben Feature-Key wie der Sidebar-Eintrag "Planung".
  const showDemandLink = Boolean(visibleDashboard && featureVisible("page.planning"));
  const showRequests = Boolean(
    visibleDashboard
    && featureVisible("page.approval_flow")
    && approvalCount > 0
  );
  const getDepartmentColor = useMemo(
    () => createDepartmentColorResolver(visibleDashboard?.departmentColorMap),
    [visibleDashboard?.departmentColorMap]
  );
  const selectedLocationName = useMemo(() => {
    const selected = locations.find((location) => String(
      location?.id ?? location?.LocationID ?? location?.location_id ?? ""
    ) === String(selectedLocationId ?? ""));
    return String(selected?.name ?? selected?.Name ?? "").trim();
  }, [locations, selectedLocationId]);
  const locationName = String(visibleDashboard?.locationName || selectedLocationName).trim();
  // Bei nur einem Standort sagt der Name nichts aus — dann steht schlicht
  // "Dashboard" im Titel, ohne Standort-Icon.
  const showLocationInTitle = locations.length > 1 && Boolean(locationName);

  const formatHours = (value, includeUnit = true) => {
    const number = asNumber(value);
    const formatted = new Intl.NumberFormat(locale, {
      maximumFractionDigits: Number.isInteger(number) ? 0 : 1,
    }).format(number);
    return includeUnit ? formatted + " " + t("dashboard.hoursShort", "Std") : formatted;
  };

  const requestMonth = (month) => {
    if (!month || month === committedMonth || loading) return;
    setDashboardRequest((current) => ({
      month,
      sequence: current.sequence + 1,
    }));
  };

  const retry = () => {
    setDashboardRequest((current) => ({
      month: loadError?.month || committedMonth || current.month,
      sequence: current.sequence + 1,
    }));
  };

  return (
    <div className="admin-dashboard-page">
      <AppWorkspace
        loading={(loading || contextIsChanging) && !visibleDashboard}
        className="admin-dashboard-workspace"
        contentClassName="admin-dashboard-workspace__content"
        title={showLocationInTitle ? (
          <DashboardLocationTitle locationName={locationName} />
        ) : t("sidebar.Dashboard", "Dashboard")}
        actions={showRequests ? (
          <OpenRequestsRow
            count={approvalCount}
            onOpen={() => navigate("/approval/flow")}
            t={t}
          />
        ) : null}
      >
        {loadError ? (
          <AppCallout
            variant="error"
            role="alert"
            title={t("dashboard.loadFailedTitle", "Dashboard konnte nicht aktualisiert werden")}
            description={t("dashboard.loadError", "Die Dashboard-Daten konnten nicht aktualisiert werden.")}
            action={(
              <AppButton variant="outlined" size="sm" onClick={retry} disabled={loading}>
                {t("dashboard.retry", "Erneut versuchen")}
              </AppButton>
            )}
            className="admin-dashboard-load-error"
          />
        ) : null}

        {visibleDashboard ? (
          <>
            {showShiftplan ? (
              <>
                <section
                  className="admin-dashboard-section"
                  aria-label={t("dashboard.liveOperations", "Live-Betrieb")}
                >
                  <div className="admin-dashboard-live-grid">
                    <CurrentShiftsCard
                      shifts={visibleDashboard.currentShifts}
                      getDepartmentColor={getDepartmentColor}
                      showShiftplanLink={showShiftplan}
                      onOpenShiftplan={() => navigate("/shiftplan")}
                      t={t}
                    />
                    <PlanningStatusCard
                      planningStatus={visibleDashboard.planningStatus}
                      getDepartmentColor={getDepartmentColor}
                      showDemandLink={showDemandLink}
                      onOpenDemand={() => navigate("/demand")}
                      locale={locale}
                      t={t}
                    />
                  </div>
                </section>

                <section
                  className="admin-dashboard-month-grid"
                  aria-label={t("dashboard.monthOverview", "Monatsübersicht")}
                >
                  <MonthlyOverviewCard
                    data={visibleDashboard.monthlyByDepartment}
                    dailyData={visibleDashboard.monthlyByDay}
                    total={visibleDashboard.monthlyTotal}
                    breakdownTotalHours={visibleDashboard.monthlyBreakdownTotal}
                    changePercent={visibleDashboard.monthlyChangePercent}
                    selectedMonth={committedMonth}
                    loading={loading}
                    onMonthChange={requestMonth}
                    getDepartmentColor={getDepartmentColor}
                    formatHours={formatHours}
                    locale={locale}
                    t={t}
                  />
                </section>
              </>
            ) : null}
          </>
        ) : null}
      </AppWorkspace>
    </div>
  );
}
